/**
 * sterntaler-variant-media-groups.js  (v2)
 * ---------------------------------------------------------------------------
 * Eigenbau-Ersatz für die App "StarApps Variant Image Automator".
 *
 * Prinzip (identisch zur App):
 *   Jede Variante hat im Backend genau EIN fest zugeordnetes Bild
 *   (variant.featured_media). Dieses Bild startet eine "Bildgruppe"; alle
 *   nachfolgenden Bilder in der natürlichen Medien-Reihenfolge gehören dazu,
 *   BIS das nächste einer Variante zugeordnete Bild kommt. Beim Farbwechsel
 *   wird nur die passende Gruppe angezeigt (Galerie + Thumbnails).
 *
 * WICHTIG (Fix ggü. v1):
 *   Das Theme zieht das featured_media der gewählten Variante per prepend nach
 *   vorne (in Liquid beim Laden UND per setActiveMedia bei jedem Klick). Die
 *   LIVE-DOM-Reihenfolge ist daher NICHT die natürliche Medien-Reihenfolge.
 *   Deshalb liest dieses Script die autoritative Reihenfolge aus einem per
 *   Liquid injizierten JSON-Block (#VariantMediaData-<sectionId>) und ordnet
 *   Bilder ausschliesslich per Media-ID zu - positionsunabhaengig und damit
 *   immun gegen das Prepend.
 *
 * Fällt der JSON-Block weg, nutzt das Script als Fallback die DOM-Reihenfolge
 * (dann wieder mit dem bekannten Prepend-Risiko) und warnt im Debug-Log.
 *
 * Aktivieren/Deaktivieren: SCRIPT_AKTIV unten auf true/false.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  /* =========================================================================
   *  KONFIGURATION
   * ========================================================================= */
  const SCRIPT_AKTIV = true; // <-- Master-Schalter. false = Script komplett aus.

  const CONFIG = {
    debug: false, // true -> Console-Logs mit Gruppen & Trefferzahlen
  };

  if (!SCRIPT_AKTIV) return;

  const log = (...a) => CONFIG.debug && console.log('%c[VariantMedia]', 'color:#0a7', ...a);
  const warn = (...a) => console.warn('%c[VariantMedia]', 'color:#c60', ...a);

  /* =========================================================================
   *  Ein Controller pro Produkt-Section
   * ========================================================================= */
  class VariantMediaGroups {
    constructor(gallery) {
      this.gallery = gallery;
      this.sectionId = gallery.id.replace(/^MediaGallery-/, '').replace(/-duplicate$/, '');

      this.viewer = gallery.querySelector('[id^="GalleryViewer"]');
      this.thumbs = gallery.querySelector('[id^="GalleryThumbnails"]');
      if (!this.viewer) return;

      this.variantSelects =
        document.querySelector(`variant-radios[data-section="${this.sectionId}"]`) ||
        document.querySelector(`variant-selects[data-section="${this.sectionId}"]`);
      if (!this.variantSelects) return;

      const data = this.readInjectedData();
      this.naturalOrder = data.order;
      this.variantFeatured = data.variantFeatured;

      if (!this.variantFeatured || this.variantFeatured.size === 0) {
        log('Keine featured_media-Zuordnungen - native Anzeige bleibt');
        return;
      }

      this.indexDomElements();
      if (this.slideByMediaId.size <= 1) return;

      if (!this.naturalOrder || !this.naturalOrder.length) {
        warn(
          `Section ${this.sectionId}: kein #VariantMediaData-Block gefunden - ` +
            `nutze DOM-Reihenfolge als Fallback (Prepend-Risiko).`
        );
        this.naturalOrder = Array.from(this.slideByMediaId.keys());
      }

      this.buildGroups();
      this.bindEvents();
      this.applyForVariant(this.getCurrentVariantId());
      log('Init Section', this.sectionId, '| Gruppen-Startbilder:', [...this.groupStartKeys]);
    }

    readInjectedData() {
      const result = { order: null, variantFeatured: new Map() };

      const el = document.getElementById(`VariantMediaData-${this.sectionId}`);
      if (el) {
        try {
          const parsed = JSON.parse(el.textContent);
          if (Array.isArray(parsed.order)) result.order = parsed.order.map(Number);
          if (Array.isArray(parsed.variantFeatured)) {
            parsed.variantFeatured.forEach((pair) => {
              const vid = pair[0];
              const mid = pair[1];
              result.variantFeatured.set(Number(vid), mid == null ? null : Number(mid));
            });
          }
        } catch (e) {
          warn('VariantMediaData-JSON konnte nicht geparst werden', e);
        }
      }

      if (result.variantFeatured.size === 0) {
        try {
          const json = this.variantSelects.querySelector('script[type="application/json"]');
          const variants = json ? JSON.parse(json.textContent) : [];
          variants.forEach((v) => {
            result.variantFeatured.set(
              Number(v.id),
              v.featured_media && v.featured_media.id ? Number(v.featured_media.id) : null
            );
          });
        } catch (e) {
          warn('Varianten-JSON konnte nicht gelesen werden', e);
        }
      }

      return result;
    }

    indexDomElements() {
      this.slideByMediaId = new Map();
      this.viewer.querySelectorAll('.product__media-item[data-media-id]').forEach((li) => {
        const mid = Number(li.dataset.mediaId.split('-').pop());
        this.slideByMediaId.set(mid, li);
      });

      this.thumbByMediaId = new Map();
      if (this.thumbs) {
        this.thumbs.querySelectorAll('[data-target]').forEach((li) => {
          const mid = Number(li.dataset.target.split('-').pop());
          this.thumbByMediaId.set(mid, li);
        });
      }
    }

    getCurrentVariantId() {
      const idInput = document.querySelector(`#product-form-${this.sectionId} input[name="id"]`);
      if (idInput && idInput.value) return Number(idInput.value);
      const v = new URL(window.location.href).searchParams.get('variant');
      if (v) return Number(v);
      const first = this.variantFeatured.keys().next();
      return first.done ? null : first.value;
    }

    buildGroups() {
      this.boundaries = new Set();
      this.variantFeatured.forEach((mid) => {
        if (mid != null) this.boundaries.add(mid);
      });

      this.groupByMedia = new Map();
      this.groupStartKeys = new Set();
      let key = 'shared';
      this.naturalOrder.forEach((mid) => {
        if (this.boundaries.has(mid)) {
          key = mid;
          this.groupStartKeys.add(mid);
        }
        this.groupByMedia.set(mid, key);
      });

      this.groupByVariant = new Map();
      this.variantFeatured.forEach((mid, vid) => {
        this.groupByVariant.set(vid, mid != null && this.boundaries.has(mid) ? mid : 'shared');
      });
    }

    bindEvents() {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this._unsub = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const d = event && event.data;
          if (!d) return;
          if (d.sectionId && d.sectionId !== this.sectionId) return;
          const variantId =
            d.variant && (d.variant.id || d.variant.value)
              ? Number(d.variant.id || d.variant.value)
              : this.getCurrentVariantId();
          requestAnimationFrame(() => this.applyForVariant(variantId));
        });
      }

      const idInput = document.querySelector(`#product-form-${this.sectionId} input[name="id"]`);
      if (idInput) {
        idInput.addEventListener('change', () =>
          requestAnimationFrame(() => this.applyForVariant(Number(idInput.value)))
        );
      }
    }

    applyForVariant(variantId) {
      if (!variantId) return;
      const activeKey = this.groupByVariant.get(variantId);
      if (activeKey === undefined) return;

      let shown = 0;
      let hidden = 0;

      this.slideByMediaId.forEach((slide, mid) => {
        const groupKey = this.groupByMedia.get(mid);
        const visible = groupKey === undefined || groupKey === activeKey || groupKey === 'shared';
        this.toggleSlide(slide, mid, visible);
        visible ? shown++ : hidden++;
      });

      log(`Variante ${variantId} -> Gruppe ${activeKey} | sichtbar ${shown}, versteckt ${hidden}`);
    }

    toggleSlide(slide, mediaId, visible) {
      slide.style.display = visible ? '' : 'none';
      if (visible) slide.removeAttribute('data-variant-hidden');
      else slide.setAttribute('data-variant-hidden', '');

      const thumb = this.thumbByMediaId.get(mediaId);
      if (thumb) thumb.style.display = visible ? '' : 'none';
    }
  }

  /* =========================================================================
   *  Bootstrap
   * ========================================================================= */
  function init() {
    document.querySelectorAll('media-gallery[id^="MediaGallery-"]').forEach((g) => {
      if (g.__variantMediaGroups) return;
      g.__variantMediaGroups = new VariantMediaGroups(g);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();